import { useCallback, useMemo, useRef } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import {
  JournalEntryType,
  apiErrorStatus,
  useCreateJournalEntry,
  useCreateJournalPhotoEntry,
  useDeleteJournalEntry,
  useGetJournal,
  useUpdateJournal,
  useUpdateJournalEntry,
} from '@zendia/frontend-shared'
import { DateTime } from 'luxon'
import { axiosRequest } from '../../../../utilities/axiosClient'
import {
  JournalEntryForm,
  type JournalFormResult,
} from '../../../../components/Journal/JournalEntryForm'

export const Route = createFileRoute('/_authenticated/journal/$journalId/edit')({
  component: RouteComponent,
  staticData: { suppressNotifications: true },
})

/**
 * Runs every task to completion, then reports the first failure. Promise.all
 * would reject on the first rejection and leave its siblings in flight, so an
 * upload could land after the save had already reported failure - and after
 * the bookkeeping that decides what a retry re-sends had been read.
 */
const settleAll = async (tasks: Array<() => Promise<void>>): Promise<void> => {
  const results = await Promise.allSettled(tasks.map((task) => task()))
  const failed = results.find((result) => result.status === 'rejected')

  if (failed?.status === 'rejected') throw failed.reason
}

function RouteComponent() {
  const { journalId } = Route.useParams()
  const router = useRouter()
  const queryClient = useQueryClient()

  const { data: journalData } = useGetJournal(axiosRequest, journalId)

  // Destructured because a mutation's mutateAsync is a stable reference while
  // the result object around it is new every render.
  const { mutateAsync: updateJournal } = useUpdateJournal(axiosRequest)
  const { mutateAsync: createJournalEntry } = useCreateJournalEntry(axiosRequest)
  const { mutateAsync: updateJournalEntry } = useUpdateJournalEntry(axiosRequest)
  const { mutateAsync: deleteJournalEntry } = useDeleteJournalEntry(axiosRequest)
  const { mutateAsync: createJournalPhotoEntry } = useCreateJournalPhotoEntry(axiosRequest)

  const journal = journalData?.data

  const textEntry = useMemo(
    () => journal?.entries?.find((entry) => entry.type === JournalEntryType.Text),
    [journal],
  )

  const images = useMemo(
    () => journal?.entries?.filter((entry) => entry.type === JournalEntryType.Photo) ?? [],
    [journal],
  )

  /**
   * A journal saved without a title is titled by its date, so an ISO-date title
   * is shown as untitled - the user never typed it.
   */
  const dateTitle = useMemo(
    () => (journal ? DateTime.fromJSDate(new Date(journal.createdAt)).toISODate() : null),
    [journal],
  )

  const initialValues = useMemo(() => {
    if (!journal) return undefined

    return {
      title: journal.title === dateTitle ? '' : journal.title,
      text: textEntry?.value ?? '',
      labels: journal.labels,
    }
  }, [journal, textEntry, dateTitle])

  const onDiscard = useCallback(() => {
    if (router.history.canGoBack()) {
      router.history.back()
    } else {
      router.history.replace(`/journal/${journalId}/detail`)
    }
  }, [journalId, router])

  /**
   * What this edit has already landed on the server. A save is several requests
   * and cannot be made atomic from here, so a failure part way through leaves
   * the form open on a journal that is partly updated. Recording the work that
   * succeeded lets the retry do only what is left, instead of re-deleting
   * entries that are already gone or re-uploading photos that are already
   * there. Written only on confirmed success, so a failed step is retried.
   */
  const savedRef = useRef<{
    textEntryId?: string
    textValue: string
    deletedImageIds: Set<string>
    uploadedFiles: Set<File>
  } | null>(null)

  /**
   * A retry re-sends deletes for entries a previous attempt already removed,
   * and the API resolves the entry before deleting it, so those come back 404.
   * Treat that as the entry being gone - the outcome we wanted.
   */
  const deleteEntryIfPresent = useCallback(
    async (entryId: string) => {
      try {
        await deleteJournalEntry({ journalId, entryId })
      } catch (error) {
        if (apiErrorStatus(error) !== 404) throw error
      }
    },
    [deleteJournalEntry, journalId],
  )

  const onSave = useCallback(
    async ({ title, text, labels, addedFiles, removedImageIds }: JournalFormResult) => {
      const trimmedTitle = title?.trim()
      const trimmedText = text?.trim() ?? ''

      // The form only renders once the journal has loaded, so the entry state
      // is known by the time a save can start.
      const saved = (savedRef.current ??= {
        textEntryId: textEntry?.id,
        textValue: textEntry?.value ?? '',
        deletedImageIds: new Set<string>(),
        uploadedFiles: new Set<File>(),
      })

      try {
        // Re-sending this is harmless: it is a full replacement of both fields.
        await updateJournal({
          journalId,
          // Clearing the title falls back to the date title, as creating one does.
          title: trimmedTitle || dateTitle || undefined,
          labels: labels ?? [],
        })

        if (saved.textEntryId) {
          if (!trimmedText) {
            await deleteEntryIfPresent(saved.textEntryId)
            saved.textEntryId = undefined
            saved.textValue = ''
          } else if (trimmedText !== saved.textValue) {
            await updateJournalEntry({
              journalId,
              entryId: saved.textEntryId,
              value: trimmedText,
            })
            saved.textValue = trimmedText
          }
        } else if (trimmedText) {
          const response = await createJournalEntry({
            journalId,
            type: JournalEntryType.Text,
            value: trimmedText,
          })

          // Without the new id a retry would create a second text entry, so a
          // response that did not carry one counts as a failure.
          if (!response.isSuccess || !response.data) {
            throw new Error('Failed to save journal entry. Please try again.')
          }

          saved.textEntryId = response.data.id
          saved.textValue = trimmedText
        }

        await settleAll([
          ...removedImageIds
            .filter((entryId) => !saved.deletedImageIds.has(entryId))
            .map((entryId) => async () => {
              await deleteEntryIfPresent(entryId)
              saved.deletedImageIds.add(entryId)
            }),
          ...addedFiles
            .filter((file) => !saved.uploadedFiles.has(file))
            .map((file) => async () => {
              await createJournalPhotoEntry({
                journalId,
                value: file,
                caption: file.name || '',
              })
              saved.uploadedFiles.add(file)
            }),
        ])
      } finally {
        // Creating entries does not invalidate on its own. Refreshing here
        // rather than only on success means a partial save still leaves the
        // detail view, and this form's images, showing what actually landed.
        await queryClient.invalidateQueries({ queryKey: ['journal'] })
      }

      onDiscard()
    },
    [
      createJournalEntry,
      createJournalPhotoEntry,
      dateTitle,
      deleteEntryIfPresent,
      journalId,
      onDiscard,
      queryClient,
      textEntry,
      updateJournal,
      updateJournalEntry,
    ],
  )

  // The form reads its initial values once, on mount, so it must not render
  // before the journal has loaded.
  if (!journal || !initialValues) return null

  return (
    <JournalEntryForm
      headerContent={DateTime.fromJSDate(new Date(journal.createdAt)).toLocaleString({
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      })}
      initialValues={initialValues}
      existingImages={images}
      saveLabel="Save changes"
      onSave={onSave}
      onDiscard={onDiscard}
    />
  )
}
